import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';

import { BaseInputProps, StyledTextField } from './StyledTextField';

export interface DateInputProps extends BaseInputProps {
  /** Valor en formato YYYY-MM-DD o ISO 8601 (ej: "2026-03-09T00:00:00.000Z") */
  value: string;
  /** Callback cuando cambia el valor */
  onChange: (value: string) => void;
  /** Fecha mínima permitida en formato YYYY-MM-DD */
  minDate?: string;
  /** Fecha máxima permitida en formato YYYY-MM-DD */
  maxDate?: string;
  /** Nombre del campo para formularios */
  name?: string;
}

/**
 * Características:
 * - Usa DatePicker de MUI con AdapterDayjs
 * - Expone y recibe siempre string en formato YYYY-MM-DD (acepta también ISO 8601 como valor de entrada)
 * - Soporte para rango de fechas min/max
 * - Reutiliza StyledTextField para mantener estilos consistentes
 */
export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  minDate,
  maxDate,
  name,
}) => {
  const dayjsValue: Dayjs | null = value ? dayjs(value) : null;

  const handleChange = (newValue: Dayjs | null) => {
    if (!newValue || !newValue.isValid()) {
      onChange('');
      return;
    }

    const formatted = newValue.format('YYYY-MM-DD');

    if (minDate && formatted < minDate) {
      return;
    }
    if (maxDate && formatted > maxDate) {
      return;
    }

    onChange(formatted);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es-mx">
      <DatePicker
        value={dayjsValue}
        onChange={handleChange}
        disabled={disabled}
        label={label}
        format="DD/MM/YYYY"
        minDate={minDate ? dayjs(minDate) : undefined}
        maxDate={maxDate ? dayjs(maxDate) : undefined}
        slots={{
          textField: StyledTextField,
        }}
        slotProps={{
          textField: {
            disabled,
            error,
            helperText,
            required,
            fullWidth,
            placeholder,
            name,
            sx: {
              '& .MuiSvgIcon-root': {
                fontSize: 18,
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
